import { findColour, findProfile, type ProfileId } from "./materials.ts";

export const PREFERENCE_EVENT = "roofviz:preference-confirmed";
export const PREFERENCE_SCHEMA = "roofviz_appearance_preference_v1" as const;

export interface RoofAppearancePreferenceV1 {
  propertyTrackingId: string;
  profile: ProfileId;
  colour: string;
  timestamp: string;
  source: "homeowner_stated";
}

interface StoredPreferenceV1 {
  schema: typeof PREFERENCE_SCHEMA;
  preference: RoofAppearancePreferenceV1;
}

export function createPreference(
  propertyTrackingId: string,
  profile: ProfileId,
  colour: string,
  timestamp = new Date().toISOString(),
): RoofAppearancePreferenceV1 {
  return {
    propertyTrackingId,
    profile,
    colour,
    timestamp,
    source: "homeowner_stated",
  };
}

export function preferenceStorageKey(propertyTrackingId: string): string {
  return `roofviz:appearance-preference:${propertyTrackingId}`;
}

export function serializePreference(preference: RoofAppearancePreferenceV1): string {
  const stored: StoredPreferenceV1 = { schema: PREFERENCE_SCHEMA, preference };
  return JSON.stringify(stored);
}

export function parsePreference(raw: string | null, expectedTrackingId?: string): RoofAppearancePreferenceV1 | null {
  if (!raw) return null;
  try {
    const stored = JSON.parse(raw) as Partial<StoredPreferenceV1>;
    const value = stored.preference;
    if (
      stored.schema !== PREFERENCE_SCHEMA ||
      !value ||
      value.source !== "homeowner_stated" ||
      typeof value.propertyTrackingId !== "string" ||
      typeof value.profile !== "string" ||
      typeof value.colour !== "string" ||
      typeof value.timestamp !== "string" ||
      !Number.isFinite(Date.parse(value.timestamp)) ||
      !findProfile(value.profile) ||
      !findColour(value.colour) ||
      (expectedTrackingId !== undefined && value.propertyTrackingId !== expectedTrackingId)
    ) {
      return null;
    }
    return value as RoofAppearancePreferenceV1;
  } catch {
    return null;
  }
}
